import classNames from 'classnames';
import hoistNonReactStatistics from 'hoist-non-react-statics';
import React from 'react';
import type { Column, MetaBase } from 'react-table';
import { TableHeaderProps, usePagination, useSortBy, useTable } from 'react-table';
import Empty from '../empty';
import { HearthBeatLoader } from '../loaders';
import Pagination from '../pagination';
import SortArrow from './components/sort-arrow';
import TableAction from './components/table-action';
import TableHeader from './components/table-header';
import { tableClasses, TableSC, TableWrapperSC } from './table.styled';
import type {
  TableTypeWorkaround,
  TChangeHandler,
  TTableCommonProps,
  TTableExtendedHeaderGroup,
  TTableExtendedState,
  TTableHook,
  TTableInitialState,
} from './table.types';

export interface TableProps<T extends object> extends TTableCommonProps {
  columns: Column<T>[];
  data: T[];

  // Table styles
  style?: React.CSSProperties;
  className?: string;

  // Table state/state change
  initialState?: TTableInitialState<T>;
  useControlledState?: (state: TTableExtendedState<T>, meta: MetaBase<T>) => TTableExtendedState<T>;
  onChange?: TChangeHandler;
  loading?: boolean;

  // Pagination props
  manualPagination?: boolean;
  autoResetPage?: boolean;
  pagination?: {
    current: number;
    total: number;
    pageSize: number;
  };

  // Table empty props
  showEmpty?: boolean;
  emptyTitle?: string;
}

const Table = <T extends object>(
  {
    columns,
    data,
    style,
    pagination,
    className,
    manualSortBy,
    showEmpty,
    loading,
    emptyTitle,
    disableMultiSort = true,
    disableSortBy = true,
    defaultCanSort = false,
    useControlledState,
    onChange,
    initialState,
    manualPagination,
    autoResetPage,
  }: TableProps<T>,
  ref?: React.Ref<HTMLTableElement>,
) => {
  const shouldFireSort = React.useRef(false);
  const shouldFirePageChange = React.useRef(false);

  const tableHook = (useTable as TTableHook<T>)(
    {
      columns,
      data,
      initialState,
      manualPagination,
      manualSortBy,
      pageCount:
        manualPagination && pagination ? Math.ceil(pagination.total / pagination.pageSize) : -1,
      disableSortBy,
      defaultCanSort,
      disableMultiSort,
      useControlledState: useControlledState as any,
    },
    useSortBy,
    usePagination,
  ) as TableTypeWorkaround<T>;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state,
    // Pagination
    gotoPage,
    setPageSize,
    pageOptions,
  } = tableHook;

  // Hack to listen to pagination changes, to set correct internal state
  React.useEffect(() => {
    if (pagination?.current) {
      gotoPage(pagination.current - 1);
      setPageSize(pagination.pageSize);
    }
  }, [pagination]);

  // Hack to listen to internal state to fire onChange handlers
  React.useEffect(() => {
    if (shouldFireSort.current) {
      onChange?.(
        { pageIndex: autoResetPage ? 1 : state.pageIndex + 1, pageSize: state.pageSize },
        state.sortBy,
      );
      shouldFireSort.current = false;
    }
  }, [state.sortBy]);

  // Hack to listen to internal state to fire onChange handlers
  React.useEffect(() => {
    if (shouldFirePageChange.current) {
      onChange?.({ pageIndex: state.pageIndex + 1, pageSize: state.pageSize }, state.sortBy);
      shouldFirePageChange.current = false;
    }
  }, [state.pageIndex, state.pageSize]);

  return (
    <TableWrapperSC style={style} className={classNames(tableClasses.component, className)}>
      <div className={tableClasses.tableContainer}>
        <TableSC {...getTableProps()} className={tableClasses.table} ref={ref}>
          <thead>
            {headerGroups.map((headerGroup, i) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                {headerGroup.headers.map((c, i: number) => {
                  const column = c as TTableExtendedHeaderGroup<T>;

                  const thProps = column.getHeaderProps(
                    column.getSortByToggleProps(),
                  ) as TableHeaderProps & { onClick?: (e: React.MouseEvent) => void };

                  return (
                    <th
                      {...thProps}
                      onClick={(e) => {
                        if (thProps.onClick) {
                          shouldFireSort.current = true;
                          thProps.onClick(e);
                        }
                      }}
                      key={i}
                      className={column.className}
                    >
                      {column.render('Header')}
                      {column.canSort ? (
                        <SortArrow isSorted={column.isSorted} isSortedDesc={column.isSortedDesc} />
                      ) : null}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} className={tableClasses.tableContent}>
            {page.length > 0 ? (
              page.map((row, i) => {
                prepareRow(row);

                return (
                  <tr {...row.getRowProps()} key={i} className={tableClasses.tableRow}>
                    {row.cells.map((cell: any, i: number) => {
                      return (
                        <td
                          {...cell.getCellProps()}
                          key={i}
                          className={classNames(tableClasses.tableCol, cell.column.className)}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : showEmpty || loading ? (
              <tr>
                <td colSpan={columns.length} className={'ui-table-empty-cell'}>
                  <Empty
                    className={tableClasses.tableEmpty}
                    title={loading ? 'Loading...' : emptyTitle}
                  />
                </td>
              </tr>
            ) : null}
          </tbody>
        </TableSC>
        {loading ? (
          <div className={tableClasses.tableLoader}>
            <HearthBeatLoader variant="on-light" loading size="medium" />
          </div>
        ) : null}
      </div>
      {pagination ? (
        <div className={tableClasses.pagination}>
          <Pagination
            current={state.pageIndex + 1}
            total={pageOptions.length * state.pageSize}
            pageSize={state.pageSize}
            onChange={(np) => {
              shouldFirePageChange.current = true;
              gotoPage(np - 1);
            }}
            onShowSizeChange={(_, size) => {
              shouldFirePageChange.current = true;
              setPageSize(size);
            }}
          />
        </div>
      ) : null}
    </TableWrapperSC>
  );
};

Table.Actions = TableAction;
Table.Header = TableHeader;

export default hoistNonReactStatistics(React.memo(React.forwardRef(Table)), Table) as typeof Table;
